/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, CSSProperties, ReactElement } from 'react';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import { getEmojiCustomFallback } from '@atlaskit/editor-common/emoji/fallback';
import { EmojiSharedCssClassName } from '@atlaskit/editor-common/styles/emoji';
import { isSingleEmoji } from '@atlaskit/editor-common/utils/isSingleEmoji';
import type { Emoji as OriginalEmoji } from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

// Compiled allows the exact relative typography and one-pixel margins used by Platform emoji.
const styles = cssMap({
	container: {
		display: 'inline-block',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Matches Platform emoji's one-pixel optical alignment.
		marginBlockStart: '-1px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Matches Platform emoji's one-pixel optical alignment.
		marginBlockEnd: '-1px',
	},
	glyph: {
		display: 'inline-block',
		verticalAlign: 'middle',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Matches Platform emoji's relative inline geometry.
		lineHeight: 1,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Dynamic emoji sizing has no equivalent typography token.
		fontSize: 'var(--light-emoji-size)',
		width: 'var(--light-emoji-size)',
		height: 'var(--light-emoji-size)',
		textAlign: 'center',
	},
	placeholder: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
	},
});

/** Use only authored Unicode accepted by Platform; never infer text from an emoji ID. */
export function getLightEmojiText({ text }: { id?: unknown; text?: unknown }): string | undefined {
	return typeof text === 'string' && isSingleEmoji(text) ? text : undefined;
}

/** Readable ADF fallback without provider subscriptions, image loading or tooltips. */
export const Emoji = ({
	id,
	shortName,
	text,
	fitToHeight,
	providers,
	lightNodeRef,
}: ComponentProps<typeof OriginalEmoji> & LightNodeProps): ReactElement => {
	const unicode = getLightEmojiText({ id, text });
	const sized = Boolean(providers?.hasProvider('emojiProvider'));
	const fallback = sized
		? unicode
		: (getEmojiCustomFallback(text || shortName) ?? (text || shortName));
	const sizeStyle: CSSProperties & { '--light-emoji-size': string } = {
		'--light-emoji-size': `var(--emoji-common-unicode-size, ${fitToHeight ?? 20}px)`,
	};
	return (
		<span
			ref={lightNodeRef}
			css={sized && styles.container}
			data-emoji-type={sized && unicode ? 'unicode' : undefined}
			data-light-emoji={unicode ? 'unicode' : 'placeholder'}
			role="img"
			aria-label={shortName}
			data-emoji-id={id}
			data-emoji-short-name={shortName}
			data-emoji-text={sized ? text : fallback}
		>
			{sized ? (
				<span
					aria-hidden="true"
					css={[styles.glyph, !unicode && styles.placeholder]}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Renderer heading selectors require Platform's shared emoji placeholder class.
					className={!unicode ? EmojiSharedCssClassName.EMOJI_PLACEHOLDER : undefined}
					style={sizeStyle}
				>
					{fallback}
				</span>
			) : (
				fallback
			)}
		</span>
	);
};
